import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useParams } from "react-router-dom";

const API_BASE = "http://127.0.0.1:8000/api";

async function parseJsonSafe(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function getAuthHeaders() {
  const accessToken = localStorage.getItem("access_token");
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

async function getList(url) {
  const res = await fetch(url, { headers: getAuthHeaders() });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data?.detail || `Failed to load ${url}`);
  return Array.isArray(data) ? data : data?.results || [];
}

async function getPatientByUhid(uhid) {
  const res = await fetch(`${API_BASE}/patients/?format=json`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data?.detail || "Failed to load patients");

  const rows = Array.isArray(data) ? data : data?.results || [];
  return rows.find((p) => String(p?.uhid) === String(uhid)) || null;
}

async function getAdmissions() {
  const res = await fetch(`${API_BASE}/patients/admissions/?format=json`, {
    headers: getAuthHeaders(),
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) throw new Error(data?.detail || "Failed to load admissions");
  return Array.isArray(data) ? data : data?.results || [];
}

function makeIdNameMap(items, idKeys, nameKeys) {
  const map = {};
  items.forEach((item) => {
    const id =
      idKeys.map((k) => item?.[k]).find((v) => v !== undefined && v !== null && v !== "") ?? null;

    const name =
      nameKeys.map((k) => item?.[k]).find((v) => v !== undefined && v !== null && v !== "") ?? "—";

    if (id !== null) {
      map[String(id)] = name;
    }
  });
  return map;
}

function displayValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

export default function PatientProfilePage() {
  const { uhid } = useParams();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [patient, setPatient] = React.useState(null);
  const [admission, setAdmission] = React.useState(null);

  const [hospitalMap, setHospitalMap] = React.useState({});
  const [departmentMap, setDepartmentMap] = React.useState({});
  const [wardMap, setWardMap] = React.useState({});
  const [roomMap, setRoomMap] = React.useState({});
  const [bedMap, setBedMap] = React.useState({});
  const [userMap, setUserMap] = React.useState({});

  React.useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");

        const [
          patientData,
          admissions,
          hospitals,
          departments,
          wards,
          rooms,
          beds,
          users,
        ] = await Promise.all([
          getPatientByUhid(uhid),
          getAdmissions(),
          getList(`${API_BASE}/hospitals/?format=json`),
          getList(`${API_BASE}/departments/?format=json`),
          getList(`${API_BASE}/wards/?format=json`),
          getList(`${API_BASE}/rooms/?format=json`),
          getList(`${API_BASE}/beds/?format=json`),
          getList(`${API_BASE}/users/?format=json`),
        ]);

        if (!patientData) {
          throw new Error("Patient not found for this UHID");
        }

        setPatient(patientData);

        const matchedAdmissions = admissions.filter((a) => {
          return (
            String(a?.patient) === String(patientData?.id) ||
            String(a?.patient_id) === String(patientData?.id) ||
            String(a?.patient?.id) === String(patientData?.id) ||
            String(a?.uhid) === String(patientData?.uhid)
          );
        });

        const latestAdmission =
          matchedAdmissions.sort((a, b) => {
            const aTime = new Date(a?.admit_at || a?.created_at || 0).getTime();
            const bTime = new Date(b?.admit_at || b?.created_at || 0).getTime();
            return bTime - aTime;
          })[0] || null;

        setAdmission(latestAdmission);

        setHospitalMap(
          makeIdNameMap(
            hospitals,
            ["organization_id", "hospital_id", "id", "uuid"],
            ["name", "organization_name", "hospital_name", "title"]
          )
        );

        setDepartmentMap(
          makeIdNameMap(
            departments,
            ["department_id", "id"],
            ["name", "department_name", "title"]
          )
        );

        setWardMap(
          makeIdNameMap(
            wards,
            ["ward_id", "id"],
            ["name", "ward_name", "unit_name", "title"]
          )
        );

        setRoomMap(
          makeIdNameMap(
            rooms,
            ["room_id", "id"],
            ["name", "room_number", "room_name", "title"]
          )
        );

        setBedMap(
          makeIdNameMap(
            beds,
            ["bed_id", "id"],
            ["name", "bed_number", "bed_name", "title"]
          )
        );

        setUserMap(
          makeIdNameMap(
            users,
            ["id", "user_id"],
            ["full_name", "name", "username", "email"]
          )
        );
      } catch (err) {
        setError(err?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [uhid]);

  function resolveHospitalName() {
    return (
      admission?.organization_name ||
      admission?.hospital_name ||
      admission?.organization?.name ||
      admission?.hospital?.name ||
      hospitalMap[
        String(
          admission?.organization ??
            admission?.organization_id ??
            admission?.hospital ??
            admission?.hospital_id ??
            patient?.organization ??
            patient?.organization_id ??
            patient?.hospital ??
            patient?.hospital_id ??
            ""
        )
      ] ||
      "—"
    );
  }

  function resolveDepartmentName() {
    return (
      admission?.department_name ||
      departmentMap[String(admission?.department ?? admission?.department_id ?? "")] ||
      admission?.department ||
      "—"
    );
  }

  function resolveWardName() {
    return (
      admission?.ward_name ||
      admission?.ward_unit ||
      wardMap[String(admission?.ward ?? admission?.ward_id ?? "")] ||
      "—"
    );
  }

  function resolveRoomName() {
    return (
      admission?.room_name ||
      admission?.room_number ||
      roomMap[String(admission?.room ?? admission?.room_id ?? "")] ||
      "—"
    );
  }

  function resolveBedName() {
    return (
      admission?.bed_name ||
      admission?.bed_number ||
      bedMap[String(admission?.bed ?? admission?.bed_id ?? "")] ||
      "—"
    );
  }

  function resolveDoctorName() {
    return (
      admission?.treating_doctor_name ||
      admission?.attending_doctor_name ||
      admission?.doctor_name ||
      userMap[String(admission?.attending_doctor ?? admission?.attending_doctor_id ?? "")] ||
      admission?.treating_doctor ||
      "—"
    );
  }

  function resolveConsultantName() {
    return (
      admission?.consultant_name ||
      userMap[String(admission?.consultant ?? admission?.consultant_id ?? "")] ||
      admission?.consultant ||
      "—"
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, background: "#f4f6f8", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 3, py: 2, background: "#f7f9fb" }}>
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                Fill basic identity and admission information.
              </Typography>
            </Box>

            <Table>
              <TableHead>
                <TableRow sx={{ background: "#dfe8f4" }}>
                  <TableCell sx={{ fontWeight: 800, width: "35%" }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Details</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Hospital Name</TableCell>
                  <TableCell>{resolveHospitalName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Full Name</TableCell>
                  <TableCell>{displayValue(patient?.full_name || patient?.name)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Age (years)</TableCell>
                  <TableCell>{displayValue(patient?.age)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Gender</TableCell>
                  <TableCell>{displayValue(patient?.sex || patient?.gender)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>UHID (Unique Health ID)</TableCell>
                  <TableCell>{displayValue(patient?.uhid)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Mobile Number</TableCell>
                  <TableCell>{displayValue(patient?.primary_phone || patient?.phone)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                  <TableCell>{displayValue(patient?.address)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Emergency Contact Name</TableCell>
                  <TableCell>{displayValue(patient?.emergency_contact_name)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Relationship</TableCell>
                  <TableCell>
                    {displayValue(
                      patient?.emergency_contact_relation || patient?.emergency_relationship
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Emergency Contact Number</TableCell>
                  <TableCell>{displayValue(patient?.emergency_contact_phone)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date/Time of Admission</TableCell>
                  <TableCell>{displayValue(admission?.admit_at)}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                  <TableCell>{resolveDepartmentName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Ward / Unit</TableCell>
                  <TableCell>{resolveWardName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Room</TableCell>
                  <TableCell>{resolveRoomName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Bed Number</TableCell>
                  <TableCell>{resolveBedName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Treating Doctor</TableCell>
                  <TableCell>{resolveDoctorName()}</TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Consultant</TableCell>
                  <TableCell>{resolveConsultantName()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}